import type { ErrorInfo, PropsWithChildren, ReactNode } from "react";
import { Component } from "react";

/**
 * Audit C-2 (docs/plans/error-empty-loading-audit-2026-05-20.md).
 *
 * Boundary now:
 *   1. logs the captured error via `componentDidCatch` (console.error in dev,
 *      forwarded to `onError` callback so future Sentry / telemetry hooks
 *      can attach without re-touching this file).
 *   2. exposes `reset()` so the user can retry without a full-page refresh.
 *      The default fallback wires that to a "重试" button.
 *   3. wraps technical detail in `<details>` so the calm friendly copy stays
 *      the primary surface, while developers / QA can still inspect the
 *      raw message + component stack.
 *
 * Consumers may pass a custom `fallback` render prop to specialize the UI for
 * a sub-tree (per-route boundaries, sheet boundaries) without copy-pasting
 * the lifecycle handling.
 */

export type ErrorBoundaryFallbackArgs = {
  error: Error;
  reset: () => void;
};

type ErrorBoundaryProps = PropsWithChildren<{
  /** Custom fallback renderer. Defaults to a friendly "重试" panel. */
  fallback?: (args: ErrorBoundaryFallbackArgs) => ReactNode;
  /** Side-effect hook for telemetry. Called whenever an error is caught. */
  onError?: (error: Error, info: ErrorInfo) => void;
}>;

type ErrorBoundaryState = {
  error: Error | null;
  info: ErrorInfo | null;
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Pick<ErrorBoundaryState, "error"> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ info });
    // Always surface in the console so dev builds get a stack trace.
    console.error("[ErrorBoundary]", error, info);
    this.props.onError?.(error, info);
  }

  reset = (): void => {
    this.setState({ error: null, info: null });
  };

  override render() {
    const { error, info } = this.state;
    if (error) {
      if (this.props.fallback) {
        return this.props.fallback({ error, reset: this.reset });
      }
      return (
        <section className="paper-panel" role="alert">
          <h2>这里出了点小问题</h2>
          <p className="error-copy">页面渲染时出了一点意外。试试重新打开看看？</p>
          <div className="error-actions">
            <button type="button" className="paper-button" onClick={this.reset}>
              重试
            </button>
          </div>
          <details className="error-details">
            <summary>技术详情</summary>
            <p>{error.message}</p>
            {info?.componentStack ? (
              <pre className="error-stack">{info.componentStack}</pre>
            ) : null}
          </details>
        </section>
      );
    }

    return this.props.children;
  }
}
